import { useFormContext } from 'react-hook-form'

export const BUDGET_METHODS = [
  { value: 'No budget', label: 'No budget', help: 'This project will not track any budget.' },
  { value: 'Fixed', label: 'Fixed', help: 'Sets a pre-established amount for this project.' },
  { value: 'Recurrent', label: 'Recurrent', help: 'The amount resets at a specified interval.' }
]

export const BUDGET_TYPES = [
  'Fixed fee',
  'Fee per task type',
  'Fee per team member',
  'Fixed hours',
  'Hours per task type',
  'Hours per team member'
]

export const BUDGET_FREQUENCIES = [
  { value: 'week', label: 'Each week' },
  { value: 'semimonth', label: 'Each semimonth' },
  { value: 'month', label: 'Each month' },
  { value: 'quarter', label: 'Each quarter' },
  { value: 'year', label: 'Each year' }
]

export const BUDGET_AMOUNT_TYPES = ['Fixed fee', 'Fixed hours']

/**
 * Presentational only. Renders its fields into the shared FormProvider owned by
 * <Settings>. Conditional fields are render-based; with `shouldUnregister` on the
 * parent form they drop out of the payload automatically when hidden.
 */
const BudgetSettings = () => {
  const {
    register,
    watch,
    formState: { errors }
  } = useFormContext()

  const method = watch('budget_method')
  const type = watch('budget_type')

  const showDetails = method !== 'No budget'
  const showAmount = showDetails && BUDGET_AMOUNT_TYPES.includes(type)
  const showFrequency = method === 'Recurrent'
  const isPerTaskOrMember = type?.includes('per task type') || type?.includes('per team member')

  return (
    <section className="col-lg-11 mt-5">
      <h3>Budget Settings</h3>
      <p>Set your budget, and we&apos;ll help you stay on track.</p>

      <h4 className="h5 mt-4 mb-2">Budget method</h4>
      <fieldset className="card py-4 px-5">
        <legend className="visually-hidden">Budget method</legend>
        {BUDGET_METHODS.map(({ value, label, help }) => {
          const id = `budgetMethod-${value.replace(/\s+/g, '-').toLowerCase()}`
          return (
            <div className="form-check mb-3" key={value}>
              <input
                className="form-check-input"
                type="radio"
                id={id}
                value={value}
                {...register('budget_method')}
              />
              <label className="form-check-label" htmlFor={id}>
                {label}
              </label>
              <div className="form-text">{help}</div>
            </div>
          )
        })}
      </fieldset>

      {showDetails && (
        <>
          <h4 className="h5 mt-5 mb-2">Budget amount</h4>
          <div className="card py-4 px-5">
            <div className="row mb-3">
              <div className="col-auto">
                <label className="form-label" htmlFor="budgetType">
                  Set budget type
                </label>
                <select className="form-select" id="budgetType" {...register('budget_type')}>
                  {BUDGET_TYPES.map((option) => (
                    <option value={option} key={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {showAmount && (
                <div className="col-auto">
                  <label className="form-label mt-4 mt-md-0" htmlFor="budgetAmount">
                    Amount
                  </label>
                  <div className="input-group">
                    {type === 'Fixed fee' && <span className="input-group-text">$</span>}
                    <input
                      type="number"
                      min="0"
                      step="any"
                      id="budgetAmount"
                      className={`form-control ${errors?.budget_amount ? 'is-invalid' : ''}`}
                      aria-label="Budget amount"
                      {...register('budget_amount', { valueAsNumber: true, min: 0 })}
                    />
                    {type === 'Fixed hours' && <span className="input-group-text">hours</span>}
                    <div className="invalid-feedback">Enter an amount of 0 or more.</div>
                  </div>
                </div>
              )}

              {isPerTaskOrMember && (
                <p className="form-text mt-3">
                  Set the{' '}
                  <b>
                    {type.includes('Fee') ? 'fee' : 'hours'}{' '}
                    {type.includes('per task type') ? 'per task type in the Tasks' : 'per team member in the Team'}
                  </b>{' '}
                  tab.
                </p>
              )}
            </div>

            {showFrequency && (
              <div className="mb-1">
                <label className="form-label" htmlFor="budgetFrequency">
                  Set frequency
                </label>
                <select className="form-select w-auto" id="budgetFrequency" {...register('budget_frequency')}>
                  {BUDGET_FREQUENCIES.map(({ value, label }) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <div className="form-text mt-3">The amount resets at the specified interval.</div>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  )
}

export default BudgetSettings
